import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../../primitives/button';
import { FormMessage } from '../../primitives/form';
import { Label } from '../../primitives/label';
import { ScrollArea, ScrollBar } from '../../primitives/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../primitives/sheet';

import { InputField } from '../input/input-field';
import { LabelWithoutRef } from '../label/label-without-ref';
import type { TFact } from './types';

type TCreateCustomFactSheetProps = {
  open: boolean;
  existingFacts: TFact[];
  factTypeOptions: string[];
  initialFact?: string;
  onOpenChange: (open: boolean) => void;
  onSave: (customFact: TFact) => void;
};

export function CreateCustomFactSheet({
  open,
  existingFacts,
  factTypeOptions,
  initialFact,
  onOpenChange,
  onSave,
}: TCreateCustomFactSheetProps) {
  const [fact, setFact] = useState('');
  const [factType, setFactType] = useState('');

  useEffect(() => {
    if (!open) return;
    setFact(initialFact?.trim() ?? '');
    setFactType('');
  }, [initialFact, open]);

  const normalizedFact = fact.trim();

  const existingFactNames = useMemo(
    () => new Set(existingFacts.map((item) => item.attributeName.trim().toLowerCase())),
    [existingFacts],
  );

  const isDuplicatedFact =
    normalizedFact.length > 0 && existingFactNames.has(normalizedFact.toLowerCase());
  const canSave = normalizedFact.length > 0 && factType.length > 0 && !isDuplicatedFact;

  const handleSave = useCallback(() => {
    if (!canSave) return;

    // Avoid a transient duplicate-warning render while parent state updates.
    setFact('');
    setFactType('');
    onOpenChange(false);

    onSave({
      allowedValues: undefined,
      attributeName: normalizedFact,
      codeDataType: factType,
      description: 'Fact personalizado',
    });
  }, [canSave, factType, normalizedFact, onOpenChange, onSave]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex size-full flex-col">
        <SheetHeader>
          <SheetTitle>Crear fact personalizado</SheetTitle>
          <SheetDescription className="text-pretty">
            Puedes crear un fact personalizado para usarlo en tus reglas. Solo necesitas especificar
            el nombre y tipo.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="grid gap-4 px-[1px]">
            <div className="space-y-1">
              <Label htmlFor="custom-fact-name">Fact</Label>
              <InputField
                autoComplete="off"
                className={isDuplicatedFact ? 'border-destructive' : undefined}
                counter={{ enable: true }}
                id="custom-fact-name"
                placeholder="my_custom_fact"
                value={fact}
                onChange={(event) => setFact(event.target.value)}
              />
              {isDuplicatedFact && <FormMessage>Este fact ya existe en la lista.</FormMessage>}
            </div>

            <div className="space-y-1">
              <LabelWithoutRef>Tipo</LabelWithoutRef>
              <Select value={factType} onValueChange={setFactType}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {factTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>

        <SheetFooter>
          <Button
            title="Cancelar"
            className="w-full"
            variant="destructive"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button title="Crear" className="w-full" disabled={!canSave} onClick={handleSave}>
            Crear
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
